import React, { useRef, useState } from "react";
import { Card, Menu, MenuItem } from "@mhome/ui";

/**
 * MenuCard - A Card component with long press interaction to show a context menu
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {Array} props.menuItems - Array of menu items: [{ label, icon, onClick, disabled?, color? }]
 * @param {Function} props.onCardClick - Optional callback when card is clicked (not long press)
 * @param {Function} props.onLongPress - Optional callback when long press is triggered
 * @param {number} props.longPressDelay - Delay in ms before showing menu (default: 800)
 * @param {number} props.scrollThreshold - Movement threshold in px to cancel long press (default: 10)
 * @param {Object} props.sx - MUI sx prop for Card styling
 * @param {number} props.elevation - Card elevation (default: 0)
 * @param {Object} props.cardProps - Additional props to pass to Card
 */
const MenuCard = ({
  children,
  menuItems = [],
  onCardClick,
  onLongPress,
  longPressDelay = 800,
  scrollThreshold = 10,
  sx = {},
  elevation = 0,
  ...cardProps
}) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const touchStateRef = useRef({
    startPos: { x: 0, y: 0 },
    startTime: 0,
    hasScrolled: false,
    longPressTriggered: false,
    pressTimer: null,
  });
  const mousePressTimerRef = useRef(null);

  // Touch handlers for mobile
  const handleTouchStart = (event) => {
    if (!event.touches || !event.touches[0]) return;

    const touch = event.touches[0];
    const touchState = touchStateRef.current;
    const targetElement = event.currentTarget;

    // Reset touch state
    touchState.startPos = { x: touch.clientX, y: touch.clientY };
    touchState.startTime = Date.now();
    touchState.hasScrolled = false;
    touchState.longPressTriggered = false;

    // Clear any existing timer
    if (touchState.pressTimer) {
      clearTimeout(touchState.pressTimer);
    }

    // Start long press timer
    touchState.pressTimer = setTimeout(() => {
      if (!touchState.hasScrolled && !touchState.longPressTriggered) {
        touchState.longPressTriggered = true;
        setMenuAnchorEl(targetElement);
        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
        // Call onLongPress callback if provided
        if (onLongPress) {
          onLongPress();
        }
      }
    }, longPressDelay);
  };

  const handleTouchMove = (event) => {
    if (!event.touches || !event.touches[0]) return;

    const touch = event.touches[0];
    const touchState = touchStateRef.current;

    if (touchState.hasScrolled) return;

    const deltaX = Math.abs(touch.clientX - touchState.startPos.x);
    const deltaY = Math.abs(touch.clientY - touchState.startPos.y);

    if (deltaX > scrollThreshold || deltaY > scrollThreshold) {
      touchState.hasScrolled = true;

      if (touchState.pressTimer) {
        clearTimeout(touchState.pressTimer);
        touchState.pressTimer = null;
      }

      if (menuAnchorEl && !touchState.longPressTriggered) {
        setMenuAnchorEl(null);
      }
    }
  };

  const handleTouchEnd = (event) => {
    const touchState = touchStateRef.current;

    if (touchState.pressTimer) {
      clearTimeout(touchState.pressTimer);
      touchState.pressTimer = null;
    }

    if (touchState.hasScrolled) {
      event.preventDefault();
      event.stopPropagation();
    }

    setTimeout(() => {
      touchState.hasScrolled = false;
      touchState.longPressTriggered = false;
    }, 100);
  };

  // Mouse handlers for desktop
  const handleMouseDown = (event) => {
    const targetElement = event.currentTarget;
    mousePressTimerRef.current = setTimeout(() => {
      setMenuAnchorEl(targetElement);
      // Call onLongPress callback if provided
      if (onLongPress) {
        onLongPress();
      }
    }, longPressDelay);
  };

  const handleMouseUp = () => {
    if (mousePressTimerRef.current) {
      clearTimeout(mousePressTimerRef.current);
      mousePressTimerRef.current = null;
    }
  };

  const handleCardClick = (event) => {
    // Prevent click if we just finished scrolling
    if (touchStateRef.current.hasScrolled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Prevent click if long press menu is showing
    if (menuAnchorEl) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Call the onCardClick callback if provided
    if (onCardClick) {
      onCardClick(event);
    }
  };

  const handleMenuClose = (event) => {
    event?.stopPropagation();
    setMenuAnchorEl(null);
    const touchState = touchStateRef.current;
    touchState.longPressTriggered = false;
    touchState.hasScrolled = false;
  };

  const handleMenuItemClick = (event, onClick) => {
    event.stopPropagation();
    if (onClick) {
      onClick(event);
    }
    handleMenuClose(event);
  };

  return (
    <>
      <Card
        elevation={elevation}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleCardClick}
        sx={{
          WebkitTapHighlightColor: "transparent",
          transition: "all 0.2s ease-in-out",
          touchAction: "manipulation",
          userSelect: "none",
          "&:active": {
            backgroundColor: "action.selected",
          },
          cursor: "pointer",
          position: "relative",
          borderRadius: "16px",
          ...sx,
        }}
        {...cardProps}
      >
        {children}
      </Card>

      {/* Context Menu */}
      {menuItems.length > 0 && (
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          {menuItems.map((item, index) => (
            <MenuItem
              key={index}
              onClick={(e) => handleMenuItemClick(e, item.onClick)}
              disabled={item.disabled}
              style={{
                fontSize: "0.875rem",
                color: item.color || "inherit",
                ...item.sx,
              }}
            >
              {item.icon && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: 8,
                  }}
                >
                  {item.icon}
                </span>
              )}
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
};

export default MenuCard;
